import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const TOPICS = [
  { t: "CRITICAL THINKING", d: "Question the default — why normal isn't always right." },
  { t: "TECHNOLOGY & ATTENTION", d: "Using the tools without letting the tools use you." },
  { t: "IDENTITY & BLACKNESS", d: "Culture, legacy, masculinity, and becoming on your own terms." },
  { t: "THE NEXT GENERATION", d: "Creators, not consumers — young people building the future." },
  { t: "MINDSET & PERSONAL DEVELOPMENT", d: "Live, grow, apply — a working philosophy for a real life." },
  { t: "SOCIETY & THE HUMAN EXPERIENCE", d: "The systems underneath everyday life, questioned out loud." },
];

const FORMATS = [
  "IN-PERSON SPEAKING ENGAGEMENTS",
  "VIRTUAL TALKS VIA ZOOM",
  "SCHOOL & YOUTH-PROGRAM PRESENTATIONS",
  "COMMUNITY & ORGANIZATIONAL EVENTS",
  "PANEL DISCUSSIONS & INTERVIEWS",
  "INTERACTIVE WORKSHOPS",
];

const VIDEOS = [
  { src: "/images/jeremiah-mind-map.jpeg", cap: "YOUR MIND IS THE MAP" },
  { src: "/images/jeremiah-start-aware.jpeg", cap: "START BEING AWARE" },
];

const PageHeader = () => (
  <header className="flex justify-between items-center px-6 sm:px-12 py-5 border-b border-blue-100/10">
    <Link to="/" data-testid="speaking-back-home" className="font-display font-extrabold text-sm tracking-tight text-aura-gradient">
      JW.
    </Link>
    <Link
      to="/book"
      data-testid="speaking-header-book"
      className="font-meta text-[10px] sm:text-xs tracking-[0.25em] bg-[#4F7CFF] text-white rounded-full px-5 py-2.5 hover:bg-white hover:text-black transition-colors duration-300"
    >
      BOOK JEREMIAH
    </Link>
  </header>
);

export default function Speaking() {
  return (
    <main className="relative bg-[#05070F] text-[#F8F8F8] min-h-screen" data-testid="speaking-page">
      <div className="aura-field" aria-hidden="true" />
      <div className="relative z-10">
        <PageHeader />

        <section className="px-6 sm:px-12 lg:px-20 pt-16 sm:pt-24 pb-20 max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <p className="font-meta text-[10px] sm:text-xs tracking-[0.35em] text-blue-300/60" data-testid="speaking-eyebrow">
              NOW BOOKING — SPEAKING ENGAGEMENTS
            </p>
            <h1
              className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-[clamp(2.4rem,8vw,7rem)] mt-6 text-aura-gradient"
              data-testid="speaking-headline"
            >
              Don't just react to life. Create a life.
            </h1>
            <p className="text-base sm:text-lg font-light leading-relaxed text-blue-100/70 mt-8 max-w-3xl" data-testid="speaking-intro">
              Jeremiah Womack is a 16-year-old speaker, writer, and social commentator. His age isn't a
              limitation — it's the perspective. He speaks as someone inside the generation everyone is
              trying to figure out: growing up with algorithms, AI, and constant noise, and choosing to
              think anyway.
            </p>
            <Link
              to="/book"
              data-testid="speaking-book-cta"
              className="inline-block mt-10 font-meta text-xs tracking-[0.3em] bg-[#4F7CFF] text-white rounded-full px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300"
            >
              BOOK JEREMIAH →
            </Link>
          </motion.div>
        </section>

        <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-7xl mx-auto border-t border-blue-100/10">
          <Reveal>
            <p className="font-meta text-[10px] sm:text-xs tracking-[0.3em] text-blue-300/60 mb-8">SPEAKING PHILOSOPHY</p>
            <p className="font-display text-xl sm:text-2xl lg:text-3xl font-bold uppercase tracking-tight leading-snug max-w-4xl" data-testid="speaking-philosophy">
              People — especially young people — don't need to be told what to think. They need to be
              reminded that they can.
            </p>
            <p className="text-base sm:text-lg font-light leading-relaxed text-blue-100/70 mt-8 max-w-3xl">
              Every talk questions systems, habits, and expectations out loud — and leaves the room with
              working tools: protect your attention, question the default, and live, grow, apply. The talks
              cover mindset, philosophy, identity, society, personal development, and the human experience —
              the same subjects as the writing, built for a live audience.
            </p>
          </Reveal>
        </section>

        <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-7xl mx-auto border-t border-blue-100/10">
          <Reveal>
            <p className="font-meta text-[10px] sm:text-xs tracking-[0.3em] text-blue-300/60 mb-8">TOPICS</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="speaking-topics">
            {TOPICS.map((t, i) => (
              <Reveal key={t.t} delay={i * 0.04} className="bg-[#05070F] p-7 sm:p-9 hover:bg-[#4F7CFF] transition-colors duration-300 group">
                <span className="font-meta text-[10px] tracking-[0.25em] text-blue-300/50 group-hover:text-white/70">0{i + 1}</span>
                <p className="font-display text-lg sm:text-xl font-bold uppercase tracking-tight mt-3">{t.t}</p>
                <p className="text-sm font-light text-blue-100/50 group-hover:text-white/80 mt-2 transition-colors duration-300">{t.d}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-7xl mx-auto border-t border-blue-100/10">
          <Reveal>
            <p className="font-meta text-[10px] sm:text-xs tracking-[0.3em] text-blue-300/60 mb-8">FORMATS</p>
          </Reveal>
          <div data-testid="speaking-formats">
            {FORMATS.map((f, i) => (
              <Reveal key={f} delay={i * 0.03}>
                <div className="flex items-baseline gap-5 sm:gap-8 border-b border-blue-100/10 py-5 px-2 sm:px-4 -mx-2 sm:-mx-4 hover:bg-[#4F7CFF] transition-colors duration-300 group">
                  <span className="font-meta text-[10px] sm:text-xs tracking-[0.25em] text-blue-300/50 group-hover:text-white/70 shrink-0">0{i + 1}</span>
                  <span className="font-display text-lg sm:text-2xl lg:text-3xl font-bold uppercase tracking-tight">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-7xl mx-auto border-t border-blue-100/10">
          <Reveal>
            <p className="font-meta text-[10px] sm:text-xs tracking-[0.3em] text-blue-300/60 mb-8">SEE THE STYLE</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 items-start" data-testid="speaking-videos">
            {VIDEOS.map((v) => (
              <figure key={v.cap} className="bg-[#05070F] group overflow-hidden">
                <div className="overflow-hidden">
                  <img src={v.src} alt={v.cap} className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-700" />
                </div>
                <figcaption className="font-meta text-[10px] tracking-[0.25em] text-neutral-500 px-4 py-3 border-t border-white/10">
                  {v.cap} — VIDEO
                </figcaption>
              </figure>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="text-sm font-light text-blue-100/60 mt-6">
              Full clips and new talks live on{" "}
              <a href="https://youtube.com/@j.3r3m1ah" target="_blank" rel="noopener noreferrer" data-testid="speaking-youtube-link" className="text-white underline underline-offset-4 hover:text-[#4F7CFF] transition-colors">
                YouTube
              </a>{" "}
              and{" "}
              <a href="https://www.instagram.com/j.erem1ah/" target="_blank" rel="noopener noreferrer" data-testid="speaking-instagram-link" className="text-white underline underline-offset-4 hover:text-[#4F7CFF] transition-colors">
                Instagram
              </a>
              .
            </p>
          </Reveal>
        </section>

        <section className="px-6 sm:px-12 lg:px-20 py-16 sm:py-24 max-w-7xl mx-auto border-t border-blue-100/10">
          <Reveal>
            <p className="font-meta text-[10px] sm:text-xs tracking-[0.3em] text-blue-300/60 mb-8">HOW BOOKING WORKS</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="speaking-steps">
            {[
              { n: "01", t: "TELL US ABOUT YOUR EVENT", d: "Fill out the booking form — organization, date, audience, topic." },
              { n: "02", t: "JEREMIAH REPLIES", d: "You'll hear back within a few days to talk details." },
              { n: "03", t: "THE TALK IS BUILT FOR YOU", d: "Every talk is shaped to your audience — students, community, or conference." },
            ].map((s) => (
              <Reveal key={s.n} className="bg-[#05070F] p-8 sm:p-10">
                <span className="font-meta text-xs tracking-[0.25em] text-blue-300/50">{s.n}</span>
                <p className="font-display text-lg sm:text-xl font-bold uppercase tracking-tight mt-3">{s.t}</p>
                <p className="text-sm font-light text-blue-100/50 mt-2">{s.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="text-center mt-16">
              <Link
                to="/book"
                data-testid="speaking-bottom-cta"
                className="inline-block font-meta text-xs sm:text-sm tracking-[0.3em] bg-[#4F7CFF] text-white rounded-full px-10 py-5 hover:bg-white hover:text-black transition-colors duration-300"
              >
                BOOK JEREMIAH FOR YOUR EVENT →
              </Link>
            </div>
          </Reveal>
        </section>

        <footer className="flex flex-col sm:flex-row justify-between gap-3 px-6 sm:px-12 lg:px-20 py-8 border-t border-white/10">
          <span className="font-meta text-[10px] tracking-[0.25em] text-neutral-600">© 2026 JEREMIAH WOMACK</span>
          <Link to="/" className="font-meta text-[10px] tracking-[0.25em] text-neutral-400 hover:text-white transition-colors">
            ← BACK TO THE SITE
          </Link>
        </footer>
      </div>
    </main>
  );
}
